import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { displayName } from "../lib/anagConfig";
import PraticaSummaryChips from "./PraticaSummaryChips";
import useCompagniaMatcher from "../lib/useCompagniaMatcher";
import NuovoProfessionistaDialog from "./NuovoProfessionistaDialog";
import { Input } from "./ui/input";
import { Loader2, Pencil, Plus, ChevronsUpDown, Unlink, KeyRound } from "lucide-react";
import { toast } from "sonner";

// Campo professionista generico con la stessa logica di SegnalatoreField:
// autocomplete predittivo sull'anagrafica {atype}, pulsante "Apri scheda" (matita)
// e "+ Crea nuovo" quando il nome digitato non esiste in anagrafica.
export default function ProAnagField({ atype, label, value, onChange, testId, compagnia, strictCompagnia, compagniaFilter, onlyFlag, excludeFlag, linkCompagnia, waCategory }) {
  const [items, setItems] = useState([]);   // lista completa caricata una sola volta
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dialog, setDialog] = useState(null); // { editRec, initialName }
  const [openUp, setOpenUp] = useState(false);
  const { inCompagnia: matchCompagnia } = useCompagniaMatcher(!!compagnia);
  const ref = useRef(null);
  // Bozza locale durante la digitazione: NON salva ad ogni carattere. Il salvataggio (onChange del
  // genitore) scatta solo alla selezione di una voce, alla creazione o alla perdita del focus se il testo è cambiato.
  const [draft, setDraft] = useState(value || "");
  useEffect(() => { setDraft(value || ""); }, [value]);
  const commitDraft = () => { const v = (draft || "").trim(); if (v !== (value || "").trim()) onChange(v); };

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // Apre verso l'alto quando lo spazio sotto è insufficiente per la tendina (max-h ~224px)
  useEffect(() => {
    if (!open || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    setOpenUp(below < 240 && r.top > below);
  }, [open, items.length, loading]);

  // Se cambia il tipo di anagrafica, invalida la cache
  useEffect(() => { setItems([]); setLoaded(false); }, [atype]);

  // Carica l'anagrafica (una volta) anche quando c'è già un valore selezionato,
  // così da poter mostrare il riepilogo contatti (telefono/email/PEC) senza aprire il dropdown.
  useEffect(() => {
    if (!value || loaded || loading) return;
    let alive = true;
    (async () => {
      try { const { data } = await api.get(`/anagrafiche/${atype}`); if (alive) { setItems(data || []); setLoaded(true); } }
      catch { if (alive) setItems([]); }
    })();
    return () => { alive = false; };
  }, [value, loaded, loading, atype]);

  const norm = (s) => (s || "").trim().toLowerCase().replace(/\s+/g, " ");
  const sortTok = (s) => norm(s).split(" ").filter(Boolean).sort().join(" ");

  // Carica l'intera anagrafica UNA SOLA VOLTA e mantiene tutto in memoria.
  const openDropdown = async () => {
    setOpen(true);
    if (loaded || loading) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/anagrafiche/${atype}`);
      setItems(data || []);
      setLoaded(true);
    } catch { setItems([]); } finally { setLoading(false); }
  };

  const findRec = (list, nome) => {
    const target = norm(nome), targetSorted = sortTok(nome);
    return (list || []).find((r) => {
      const dn = norm(displayName(r));
      const nc = norm([r.nome, r.cognome].filter(Boolean).join(" "));
      const cn = norm([r.cognome, r.nome].filter(Boolean).join(" "));
      const den = norm(r.denominazione);
      if (dn === target || nc === target || cn === target || (den && den === target)) return true;
      const rs = sortTok([r.nome, r.cognome].filter(Boolean).join(" "));
      return rs && rs === targetSorted;
    });
  };

  const openScheda = async () => {
    const nome = (draft || value || "").trim();
    if (!nome) return;
    try {
      const { data } = await api.get(`/anagrafiche/${atype}`);
      const rec = findRec(data, nome);
      if (!rec) { toast.error(`Scheda ${label || "professionista"} non trovata in anagrafica`); return; }
      setDialog({ editRec: rec, initialName: nome });
    } catch { toast.error(`Impossibile aprire la scheda ${label || "professionista"}`); }
  };

  const strict = !!strictCompagnia;
  const filterByComp = strict || !!compagniaFilter; // mostra solo gli associati quando la compagnia è selezionata

  const compName = (c) => (typeof c === "string" ? c : (c?.denominazione || c?.nome || c?.name || c?.label || ""));
  const inCompagnia = (i) => {
    if (!compagnia) return false;
    const list = Array.isArray(i.compagnie) ? i.compagnie : (Array.isArray(i.compagnie_multi) ? i.compagnie_multi : []);
    return matchCompagnia(list, compagnia);
  };

  // Filtro base per flag (es. solo periti di parte)
  const base = onlyFlag ? items.filter((r) => !!r[onlyFlag]) : (excludeFlag ? items.filter((r) => !r[excludeFlag]) : items);
  const noMatch = !!(draft && draft.trim().length >= 2 && !base.some((r) => norm(displayName(r)) === norm(draft)));

  // Filtro client-side istantaneo: se il valore è già un record selezionato mostra tutta la lista,
  // altrimenti filtra per testo digitato (nome/cognome/ragione sociale/denominazione).
  const qNorm = norm(draft);
  const exactSelected = !!qNorm && base.some((r) => norm(displayName(r)) === qNorm);
  const activeQuery = qNorm && !exactSelected ? qNorm : "";
  const matchQuery = (r) => {
    if (!activeQuery) return true;
    const hay = norm([displayName(r), r.nome, r.cognome, r.denominazione, r.ragione_sociale].filter(Boolean).join(" "));
    return hay.includes(activeQuery);
  };
  const pool = base.filter(matchQuery);
  const assoc = (!onlyFlag && compagnia) ? pool.filter(inCompagnia) : [];
  const others = onlyFlag
    ? pool                                                       // solo flag: lista piatta senza raggruppamento compagnia
    : (filterByComp
      ? (compagnia ? [] : pool)                                  // filtro compagnia: solo associati se compagnia; tutti se nessuna
      : (compagnia ? pool.filter((i) => !inCompagnia(i)) : pool));
  const needCompagnia = strict && !compagnia;                    // solo Perito: nasconde tutto senza compagnia
  const noAssoc = filterByComp && !!compagnia && !loading && assoc.length === 0;
  const noFlag = !!onlyFlag && loaded && !loading && base.length === 0;
  const showCrea = !loading;
  const dropdownOpen = open;

  // Record selezionato (match per nome) per mostrare il riepilogo contatti sotto il campo.
  const selectedRec = (value || "").trim() ? findRec(items, value) : null;

  const renderOpt = (r) => (
    <button key={r.id} type="button" onMouseDown={(e) => { e.preventDefault(); setDraft(displayName(r)); onChange(displayName(r)); setOpen(false); }} data-testid={`${testId}-opt`}
      className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-slate-800/60 border-b border-slate-800 last:border-0 flex items-center gap-2"><span className="truncate flex-1">{displayName(r)}</span>{r.portale_attivo && <KeyRound className="h-3.5 w-3.5 text-amber-500 shrink-0" aria-label="Accesso portale Web attivo" />}</button>
  );

  return (
    <div className="mt-1 relative" ref={ref}>
      <div className="flex items-center gap-1.5">
        <div className="relative flex-1">
          <Input value={draft} onChange={(e) => { setDraft(e.target.value); setOpen(true); }} onFocus={openDropdown} onBlur={commitDraft}
            data-testid={testId} className={`h-9 bg-slate-900/60 border-slate-700 ${selectedRec?.portale_attivo ? "pr-14" : "pr-8"}`} placeholder={`Seleziona o cerca ${label || "professionista"}…`} autoComplete="off" />
          {selectedRec?.portale_attivo && (
            <span className="absolute right-8 top-1/2 -translate-y-1/2 text-amber-500" title="Ha l'accesso al portale incarichi Web" aria-label="Accesso portale Web attivo" data-testid={`${testId}-portale-key`}><KeyRound className="h-4 w-4" /></span>
          )}
          <button type="button" tabIndex={-1} onClick={() => (open ? setOpen(false) : openDropdown())} data-testid={`${testId}-toggle`}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><ChevronsUpDown className="h-4 w-4" /></button>
          {dropdownOpen && (
            <div className={`absolute z-50 w-full rounded-md border border-slate-700 bg-[#121824] shadow-xl max-h-56 overflow-y-auto ${openUp ? "bottom-full mb-1" : "top-full mt-1"}`} data-testid={`${testId}-list`}>
              {loading && <div className="px-3 py-2 text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Ricerca…</div>}
              {needCompagnia && !loading && (
                <div className="px-3 py-2 text-[11px] font-semibold text-amber-400 bg-amber-500/10" data-testid={`${testId}-need-compagnia`}>Seleziona prima la compagnia assicurativa</div>
              )}
              {!loading && !needCompagnia && compagnia && assoc.length > 0 && (
                <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-amber-400 bg-amber-500/10 sticky top-0" data-testid={`${testId}-hdr-assoc`}>Associati a {compagnia}</div>
              )}
              {!loading && !needCompagnia && assoc.map(renderOpt)}
              {noAssoc && (
                <div className="px-3 py-2 text-[11px] font-semibold text-amber-400 bg-amber-500/10" data-testid={`${testId}-noassoc`}>Nessun {label || "perito"} associato</div>
              )}
              {!loading && !needCompagnia && !filterByComp && assoc.length > 0 && others.length > 0 && (
                <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400 bg-slate-800/60 border-t border-slate-700" data-testid={`${testId}-hdr-altri`}>Altri {label || "professionisti"}</div>
              )}
              {!loading && !needCompagnia && others.map(renderOpt)}
              {noFlag && (
                <div className="px-3 py-2 text-[11px] font-semibold text-amber-400 bg-amber-500/10" data-testid={`${testId}-noflag`}>Nessun {label || "perito"} disponibile</div>
              )}
              {showCrea && (
                <button type="button" onMouseDown={(e) => { e.preventDefault(); const preset = {}; if (onlyFlag) preset[onlyFlag] = true; if (linkCompagnia && (compagnia || "").trim()) preset.compagnie = [compagnia.trim()]; setDialog({ editRec: null, initialName: noMatch ? draft : "", preset: Object.keys(preset).length ? preset : null }); setOpen(false); }} data-testid={`${testId}-crea`}
                  className="sticky bottom-0 w-full text-left px-3 py-2 text-sm font-medium text-teal-300 bg-[#121824] hover:bg-[#15302f] flex items-center gap-1.5 border-t border-slate-700"><Plus className="h-4 w-4 shrink-0" /> <span className="truncate">Crea nuovo {label || "professionista"}{noMatch ? <> “{draft.trim()}”</> : null}…</span></button>
              )}
            </div>
          )}
        </div>
        {draft && draft.trim() && (
          <button type="button" onClick={openScheda} data-testid={`${testId}-edit`} title={`Modifica ${label || "professionista"}`}
            className="shrink-0 h-9 w-9 flex items-center justify-center rounded-md border border-teal-500/40 text-teal-600 hover:bg-teal-500/10"><Pencil className="h-4 w-4" /></button>
        )}
        {atype === "periti" && (value || "").trim() && (
          <button type="button" onClick={() => { if (!window.confirm(`Scollegare il ${label || "perito"} "${value}" da questa pratica?`)) return; setDraft(""); setOpen(false); onChange(""); }}
            data-testid={`${testId}-unlink`} title={`Scollega ${label || "perito"}`} aria-label={`Scollega ${label || "perito"}`}
            className="shrink-0 h-9 w-9 flex items-center justify-center rounded-md border border-red-500/40 text-red-500 hover:bg-red-500/10"><Unlink className="h-4 w-4" /></button>
        )}
      </div>
      {selectedRec && <PraticaSummaryChips rec={selectedRec} testId={testId} waCategory={waCategory} />}
      {dialog && (
        <NuovoProfessionistaDialog open={true} atype={atype} label={label || "Professionista"} editRec={dialog.editRec} initialName={dialog.initialName} preset={dialog.preset}
          onSaved={(rec) => { if (rec && rec.id) setItems((prev) => [...(prev || []).filter((r) => r.id !== rec.id), rec]); setDraft(displayName(rec)); onChange(displayName(rec)); setDialog(null); }} onCancel={() => setDialog(null)} />
      )}
    </div>
  );
}
